import type { User } from "@/pages/Public/pages/Login/schema/login";

/** The parts of a sender (a draft's `from`, a picked schedule sender) the confirmation reads. */
export interface SenderIdentity {
	userId: number;
	type: "email" | "linkedin";
	emailAddress?: string;
	/** A display name the caller already holds; looked up among the sending users otherwise. */
	name?: string;
	/** The option label, the last resort for a name ("Anna Müller (LinkedIn)"). */
	label?: string;
}

/** One sender who is not the current user, ready to show: "Anna Müller (anna@example.com)". */
export interface OtherSender {
	key: string;
	description: string;
}

/**
 * Whether a message from `sender` goes out as someone other than the current user (PREC-4312).
 * The one place every send, schedule and save path asks. A sender with no user (`userId` 0, the
 * "Select sender" placeholder) is nobody yet, not someone else. My own secondary mailbox is me.
 */
export const isSomeoneElse = (
	sender: Pick<SenderIdentity, "userId"> | null | undefined,
	currentUserId: number | null | undefined
): boolean => !!sender?.userId && sender.userId !== currentUserId;

const nameFromLabel = (label: string | undefined): string | undefined =>
	label?.split(" (")[0]?.trim() || undefined;

const nameOf = (sender: SenderIdentity, sendingUsers: User[] | undefined): string => {
	const user = sendingUsers?.find((u) => u.id === sender.userId);
	const fromUser = user ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() : "";

	return sender.name || fromUser || nameFromLabel(sender.label) || `#${sender.userId}`;
};

/** "Anna Müller (anna@example.com)", or "Anna Müller (LinkedIn)" for a LinkedIn sender. */
export const describeSender = (sender: SenderIdentity, sendingUsers: User[] | undefined): string =>
	`${nameOf(sender, sendingUsers)} (${
		sender.type === "email" ? (sender.emailAddress ?? "email") : "LinkedIn"
	})`;

/**
 * Every sender among `senders` who is not the current user, each once, in the order they first
 * appear: a batch of drafts is confirmed once, listing each other sender once (PREC-4312, Q5).
 */
export const listOtherSenders = (
	senders: (SenderIdentity | null | undefined)[],
	currentUserId: number | null | undefined,
	sendingUsers: User[] | undefined
): OtherSender[] => {
	const seen = new Map<string, OtherSender>();

	senders.forEach((sender) => {
		if (!sender || !isSomeoneElse(sender, currentUserId)) {
			return;
		}

		const key = `${sender.userId}:${sender.type}:${sender.type === "email" ? sender.emailAddress : ""}`;

		if (!seen.has(key)) {
			seen.set(key, { key, description: describeSender(sender, sendingUsers) });
		}
	});

	return [...seen.values()];
};

/**
 * The question the confirmation asks, as an i18n key and its values. One sender is named in the
 * sentence ("Send this message as Anna Müller (anna@example.com)?"); several are listed under it.
 */
export const senderConfirmationQuestion = (
	others: OtherSender[],
	messageCount: number
): { key: string; values: Record<string, string> } => {
	if (others.length > 1) {
		return { key: "inbox.identity.confirmSeveral", values: {} };
	}

	return {
		key: messageCount > 1 ? "inbox.identity.confirmMany" : "inbox.identity.confirmOne",
		values: { sender: others[0]?.description ?? "" },
	};
};
